import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/**
 * [D239] Field-level errors from the server, shown under the input they are about.
 *
 * A 422 names fields by their API key (`individual_npi`); a `<Field>` knows its label
 * ("Individual NPI"). Both normalise to the same letters-and-digits key, so a form gets its
 * messages under the right inputs without every call site mapping one to the other. A field
 * whose label does not match can name its key with `<Field name="…">`. Whatever matches nothing
 * is still shown: the error pop-up lists every field message.
 */
export function fieldKey(value: string): string {
  // The whole path: `guarantor.phone` is not the form's "Phone", and `lines[1].paid` is not
  // `lines[0].paid`. A nested key that matches no label is listed in the pop-up instead.
  return value.toLowerCase().replace(/[^a-z0-9]/g, "");
}

interface FieldErrorsApi {
  errors: ReadonlyMap<string, string>;
  set: (errors: ReadonlyMap<string, string>) => void;
}

const FieldErrorsContext = createContext<FieldErrorsApi | null>(null);

/** Provided by every `FormDialog`; anything inside may publish or read field errors. */
export function FieldErrorsProvider({ children }: { children: ReactNode }) {
  const [errors, setErrors] = useState<ReadonlyMap<string, string>>(
    () => new Map(),
  );
  const set = useCallback(
    (next: ReadonlyMap<string, string>) => setErrors(next),
    [],
  );
  const api = useMemo(() => ({ errors, set }), [errors, set]);
  return (
    <FieldErrorsContext.Provider value={api}>
      {children}
    </FieldErrorsContext.Provider>
  );
}

/** The publisher's half: `null` outside a provider, where there is nowhere to show them. */
export function useFieldErrorsPublisher():
  ((errors: ReadonlyMap<string, string>) => void) | null {
  return useContext(FieldErrorsContext)?.set ?? null;
}

/** The server's message for this field, by its explicit name or by its label. */
export function useServerFieldError(
  label: string,
  name?: string,
): string | undefined {
  const errors = useContext(FieldErrorsContext)?.errors;
  if (errors === undefined || errors.size === 0) return undefined;
  return (
    errors.get(fieldKey(name ?? label)) ??
    (name ? errors.get(fieldKey(label)) : undefined)
  );
}
